import { FC, ReactNode } from 'react';
import { ModalComponent } from 'components/Modal';

interface EmbedBadgePopupProps {
  isOpen: boolean;
  onClose: () => void;
  snippet: string;
  onCopy: () => void;
}

const CODE_TOKEN_REGEX = /"[^"]*"|[a-zA-Z-]+(?==)/g;

const highlightSnippet = (snippet: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  let cursor = 0;

  Array.from(snippet.matchAll(CODE_TOKEN_REGEX)).forEach((match, index) => {
    const token = match[0];
    const start = match.index ?? 0;

    if (start > cursor) {
      nodes.push(snippet.slice(cursor, start));
    }

    const className = token.startsWith('"') ? 'badge-popup__code-value' : 'badge-popup__code-attr';
    nodes.push(
      <span key={index} className={className}>
        {token}
      </span>
    );
    cursor = start + token.length;
  });

  if (cursor < snippet.length) {
    nodes.push(snippet.slice(cursor));
  }

  return nodes;
};

export const EmbedBadgePopup: FC<EmbedBadgePopupProps> = ({ isOpen, onClose, snippet, onCopy }) => {
  const handleCopyAndClose = () => {
    onCopy();
    onClose();
  };

  return (
    <ModalComponent
      isOpen={isOpen}
      closeModal={onClose}
      contentClassName="badge-popup"
      contentLabel="Embed your badge"
    >
      <span className="badge-popup__title">Embed your badge</span>
      <span className="badge-popup__description">
        Copy the code below and send it to whoever manages your website. It places your Impact Score
        badge on your site and links directly to your Oath fundraising page.
      </span>
      <pre className="badge-popup__code">{highlightSnippet(snippet)}</pre>
      <div className="badge-popup__instructions">
        <span className="badge-popup__instructions-title">Instructions for your web designer</span>
        <span className="badge-popup__instructions-description">
          Paste this snippet anywhere on the site — the footer or a sidebar both work well. No extra
          script or plugin needed. Re-copy this code any time you download a new badge.
        </span>
      </div>
      <div className="badge-popup__actions">
        <button type="button" className="btn-inkBlue-inverted btn btn-secondary" onClick={onClose}>
          <span className="body-small-text">Close</span>
        </button>
        <button
          type="button"
          className="btn-oathBlue btn btn-secondary"
          onClick={handleCopyAndClose}
        >
          <span className="body-small-text text-white">Copy Code</span>
        </button>
      </div>
    </ModalComponent>
  );
};
